import React, { useEffect, useMemo } from 'react';
import type { WorkspaceMode } from './config';
import {
  DEFAULT_CODING_SCREEN_CONFIG,
  DEFAULT_GRADING_SCREEN_CONFIG
} from './config';
import {
  createUiEventBus,
  installConsole,
  installKeyboardBridge
} from './events';
import type {
  CodingScreenApi,
  CodingScreenState,
  PanelContent
} from './interface';
import { CodingScreenProvider } from './interface';
import { CaseNotesActivityPanelProvider } from './molecules/CaseNotesActivityPanelContext';
import type { SlotRegistry } from './registry';
import { DEFAULT_SLOT_REGISTRY, SlotRegistryProvider } from './registry';
import { CodingScreenTemplate } from './templates/CodingScreenTemplate';

/**
 * The public mount point: supplies its OWN `CodingScreenProvider` -- the shell
 * is the ONE place in this folder that constructs a config and hands it down; every descendant
 * reads it back out via `useCodingConfig()`. Accepts OPTIONAL `state`/`api`/`panelContent` props and
 * forwards them into that provider; all are `undefined` when omitted (the empty shell renders fine
 * with none supplied). A dashboard-ui-side adapter (living OUTSIDE this folder, so it may see
 * redux/Apollo) maps real values from the app and injects them through these props.
 *
 * `mode` (default `'coding'`) selects which hard-coded default config to render from -- the SAME
 * shell serves both the coding bench and CQA grading (grading is a bench MODE, not a
 * separate screen). The empty shell renders identically either way; only `CodingScreenConfig.mode`
 * (and therefore `CodingScreenTemplate`'s `data-mode` attribute) differs. A routed caller passes the
 * prop via `render={() => <CodingScreenShell mode="grading" />}` (a `<Route component=...>` cannot
 * pass props) -- see routing.ts.
 *
 * `registry` is OPTIONAL and defaults to `DEFAULT_SLOT_REGISTRY` (the in-folder
 * production registry) -- a dashboard-ui-side adapter living OUTSIDE this folder injects its OWN,
 * WIDER registry (composed from `DEFAULT_SLOT_REGISTRY` plus out-of-folder slot components, e.g.
 * `NoteNav`/`NoteViewer`) through this prop so real note-rendering components can resolve without
 * this folder ever importing them directly.
 */
export const CodingScreenShell: React.FC<{
  mode?: WorkspaceMode;
  state?: CodingScreenState;
  api?: CodingScreenApi;
  panelContent?: PanelContent;
  registry?: SlotRegistry;
}> = ({
  mode = 'coding',
  state,
  api,
  panelContent,
  registry = DEFAULT_SLOT_REGISTRY
}) => {
  const bus = useMemo(() => createUiEventBus(), []);
  const config =
    mode === 'grading'
      ? DEFAULT_GRADING_SCREEN_CONFIG
      : DEFAULT_CODING_SCREEN_CONFIG;
  useEffect(() => {
    const uninstallBridge = installKeyboardBridge(
      bus,
      config.keyboard?.bindings ?? []
    );
    const uninstallConsole = installConsole(bus);
    return () => {
      uninstallConsole();
      uninstallBridge();
    };
  }, [bus, config]);
  return (<SlotRegistryProvider registry={registry}>

    <CodingScreenProvider
      config={config}
      bus={bus}
      state={state}
      api={api}
      panelContent={panelContent}
    >
      <CaseNotesActivityPanelProvider>
        <CodingScreenTemplate />
      </CaseNotesActivityPanelProvider>
    </CodingScreenProvider>
  </SlotRegistryProvider>)
};

export default CodingScreenShell;
